import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format, formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Building, MapPin, Clock, Euro, Users, TrendingUp, AlertTriangle,
  Gavel, ShoppingCart, ChevronLeft, ChevronRight, Eye, Timer, Zap
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Countdown Timer Component
const CountdownTimer = ({ endTime, onExpired }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = differenceInSeconds(end, now);
      
      if (diff <= 0) {
        onExpired?.();
        return { hours: 0, minutes: 0, seconds: 0, expired: true };
      }
      
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      
      return { hours, minutes, seconds, expired: false, isUrgent: diff < 300 };
    };
    
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [endTime, onExpired]);
  
  if (!timeLeft) return null;
  
  if (timeLeft.expired) {
    return <span className="text-red-600 font-bold">Terminata</span>;
  }
  
  return (
    <div className={`flex items-center gap-1 font-mono ${timeLeft.isUrgent ? 'text-red-600 animate-pulse' : 'text-gray-700'}`}>
      <Timer className={`h-4 w-4 ${timeLeft.isUrgent ? 'text-red-600' : 'text-gray-500'}`} />
      <span className="font-bold">
        {String(timeLeft.hours).padStart(2, '0')}:
        {String(timeLeft.minutes).padStart(2, '0')}:
        {String(timeLeft.seconds).padStart(2, '0')}
      </span>
      {timeLeft.isUrgent && <Zap className="h-4 w-4 text-red-600" />}
    </div>
  );
};

// Competition Card Component
const CompetitionCard = ({ competition, onViewDetails }) => {
  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-800',
    active: 'bg-green-100 text-green-800',
    closing: 'bg-yellow-100 text-yellow-800',
    suspended: 'bg-red-100 text-red-800'
  };
  
  const statusLabels = {
    scheduled: 'Programmata',
    active: 'In Corso',
    closing: 'In Chiusura',
    suspended: 'Sospesa'
  };
  
  const isActive = competition.status === 'active';
  const photo = competition.property?.photos?.[0] || '/placeholder-property.jpg';
  
  return (
    <div 
      className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group"
      data-testid={`gara-card-${competition.competition_id}`}
    >
      {/* Image */}
      <div className="relative h-40 sm:h-48 overflow-hidden">
        <img 
          src={photo} 
          alt={competition.property?.address || 'Immobile'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
          <Badge className={`text-xs sm:text-sm ${statusColors[competition.status] || 'bg-gray-100 text-gray-800'}`}>
            {statusLabels[competition.status] || competition.status}
          </Badge>
        </div>
        {isActive && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 sm:px-3 sm:py-1.5 shadow-md">
            <CountdownTimer endTime={competition.end_datetime} />
          </div>
        )}
        {competition.buy_now_available && (
          <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3">
            <Badge className="bg-orange-500 text-white flex items-center gap-1 text-xs">
              <ShoppingCart className="h-3 w-3" />
              <span className="hidden sm:inline">Acquista Subito</span>
              <span className="sm:hidden">Subito</span>
            </Badge>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4 sm:p-5">
        {/* Location */}
        <div className="flex items-center text-gray-500 text-sm mb-2">
          <MapPin className="h-4 w-4 mr-1" />
          <span>{competition.property?.city || 'N/A'}, {competition.property?.province || ''}</span>
        </div>
        
        {/* Address */}
        <h3 className="font-semibold text-lg text-gray-900 mb-3 line-clamp-1">
          {competition.property?.address || 'Indirizzo non disponibile'}
        </h3>
        
        {/* Prices */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Valore Immobile:</span>
            <span className="font-medium text-gray-700">
              €{competition.valore_immobile?.toLocaleString('it-IT')}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Base di Gara:</span>
            <span className="font-bold text-[#0071e3]">
              €{competition.base_di_gara?.toLocaleString('it-IT')}
            </span>
          </div>
          {competition.current_highest_bid && (
            <div className="flex justify-between items-center bg-green-50 -mx-2 px-2 py-1 rounded">
              <span className="text-sm text-green-700 flex items-center">
                <TrendingUp className="h-4 w-4 mr-1" />
                Offerta Attuale:
              </span>
              <span className="font-bold text-green-700">
                €{competition.current_highest_bid?.toLocaleString('it-IT')}
              </span>
            </div>
          )}
        </div>
        
        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center">
            <Users className="h-4 w-4 mr-1" />
            <span>{competition.total_bids || 0} offerte</span>
          </div>
          {competition.buy_now_price && (
            <div className="flex items-center text-orange-600">
              <ShoppingCart className="h-4 w-4 mr-1" />
              <span>€{competition.buy_now_price?.toLocaleString('it-IT')}</span>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <Button 
          className="w-full bg-[#0071e3] hover:bg-[#0a3540]"
          onClick={() => onViewDetails(competition.competition_id)}
          data-testid={`view-gara-${competition.competition_id}`}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizza Dettagli
        </Button>
      </div>
    </div>
  );
};

// Main Page Component
const GareDiVenditaPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const fetchCompetitions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/gara/active`, {
        params: { page, limit: 12 }
      });
      setCompetitions(response.data.competitions || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Errore nel caricamento delle gare');
    } finally {
      setLoading(false);
    }
  }, [page]);
  
  useEffect(() => {
    fetchCompetitions();
  }, [fetchCompetitions]);
  
  const handleViewDetails = (competitionId) => {
    navigate(`/gara/${competitionId}`);
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0071e3] to-[#1a6b7d] text-white py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ">
              Gare di Vendita
            </h1>
            <p className="text-base sm:text-xl text-white/80 max-w-2xl mx-auto px-4">
              Partecipa alle gare competitive per acquistare immobili a prezzi vantaggiosi.
            </p>
          </div>
          
          {/* Stats */}
          <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-2 sm:gap-4 max-w-xl mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold">{total}</div>
              <div className="text-xs sm:text-sm text-white/70">Gare Attive</div>
            </div>
            <div className="text-center border-x border-white/20">
              <div className="text-2xl sm:text-3xl font-bold">100%</div>
              <div className="text-xs sm:text-sm text-white/70">Trasparenza</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold">24/7</div>
              <div className="text-xs sm:text-sm text-white/70">Accessibilità</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Info Banner */}
        {!user && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-blue-600 mr-3" />
              <span className="text-blue-800">
                Per partecipare alle gare devi registrarti come acquirente e versare il deposito cauzionale.
              </span>
            </div>
            <Link to="/registrazione/acquirente">
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
                Registrati
              </Button>
            </Link>
          </div>
        )}
        
        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-5 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-10 bg-gray-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : competitions.length === 0 ? (
          <div className="text-center py-16">
            <Gavel className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nessuna gara attiva al momento
            </h3>
            <p className="text-gray-500">
              Torna presto per scoprire nuove opportunità di acquisto.
            </p>
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gare-grid">
              {competitions.map((comp) => (
                <CompetitionCard 
                  key={comp.competition_id}
                  competition={comp}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-10">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Precedente
                </Button>
                <span className="text-gray-600">
                  Pagina {page} di {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Successiva
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GareDiVenditaPage;
